import React, { useEffect, useMemo, useState } from "react";
import { Box, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Typography } from "@mui/material";
import ExerciseProgressChart from "./ExerciseProgressChart";

const TOP_CHART_COUNT = 3;

/**
 * Top exercises by session count as a row of charts; remaining exercises in a spillover selector.
 */
function ExerciseProgressExplorer({ aggregated, exercisesWithProgression, weightUnit = "lbs" }) {
  const options = useMemo(() => {
    const list = exercisesWithProgression ?? [];
    return list.filter((e) => e.exerciseId);
  }, [exercisesWithProgression]);

  const topCharts = useMemo(() => options.slice(0, TOP_CHART_COUNT), [options]);
  const spilloverOptions = useMemo(() => options.slice(TOP_CHART_COUNT), [options]);

  const [spillExerciseId, setSpillExerciseId] = useState("");

  useEffect(() => {
    if (spilloverOptions.length === 0) {
      setSpillExerciseId("");
      return;
    }
    const ids = new Set(spilloverOptions.map((o) => o.exerciseId));
    if (!spillExerciseId || !ids.has(spillExerciseId)) {
      setSpillExerciseId(spilloverOptions[0].exerciseId);
    }
  }, [spillExerciseId, spilloverOptions]);

  if (!options.length) {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: (t) => `1px dashed ${t.palette.divider}`,
          bgcolor: "grey.50",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Log the same exercise on at least two days to see max-weight trend charts.
        </Typography>
      </Box>
    );
  }

  const spillCurrent =
    spilloverOptions.find((e) => e.exerciseId === spillExerciseId) ?? spilloverOptions[0];

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
        Exercise progress
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Max weight per session for your most-logged exercises (last 90 days). Use{" "}
        <strong>More exercises</strong> to open any other exercise with enough history.
      </Typography>

      <Grid container spacing={2}>
        {topCharts.map((e) => (
          <Grid item xs={12} sm={6} md={4} key={e.exerciseId}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                height: "100%",
                borderRadius: 2,
                border: (t) => `1px solid ${t.palette.divider}`,
                bgcolor: "background.paper",
                minWidth: 0,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="text.primary"
                sx={{ mb: 1, lineHeight: 1.3 }}
                title={e.exerciseName || ""}
              >
                {e.exerciseName || "Exercise"}
              </Typography>
              <ExerciseProgressChart
                exerciseId={e.exerciseId}
                aggregated={aggregated}
                weightUnit={weightUnit}
                fullWidth
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      {spilloverOptions.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>
            More exercises
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Choose any other exercise you&apos;ve logged on two or more days.
          </Typography>
          <FormControl fullWidth size="small" sx={{ maxWidth: 480, mb: 2 }}>
            <InputLabel id="exercise-progress-spill-label">Exercise</InputLabel>
            <Select
              labelId="exercise-progress-spill-label"
              label="Exercise"
              value={spillExerciseId || spillCurrent?.exerciseId}
              onChange={(ev) => setSpillExerciseId(ev.target.value)}
            >
              {spilloverOptions.map((e) => (
                <MenuItem key={e.exerciseId} value={e.exerciseId}>
                  {e.exerciseName || "Exercise"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {spillCurrent ? (
            <ExerciseProgressChart
              exerciseId={spillCurrent.exerciseId}
              aggregated={aggregated}
              weightUnit={weightUnit}
              fullWidth
            />
          ) : null}
        </Box>
      )}
    </Box>
  );
}

export default ExerciseProgressExplorer;
